import React, { useState } from 'react';
import {
  Droplet,
  Dna,
  HeartPulse,
  TestTube,
  FlaskConical,
  Pill,
  Microscope,
  Activity,
  X,
  CheckCircle,
  CreditCard,
  Calendar,
  Lock,
  ShieldCheck,
  Wind,
  Syringe,
  Leaf,
  Bone,
  Sun,
  Radiation
} from 'lucide-react';

const IndividualTests = () => {
  // حالة لإظهار/إخفاء التحاليل الإضافية
  const [showAllTests, setShowAllTests] = useState(false);
  // حالة لإظهار/إخفاء النافذة المنبثقة
  const [isModalOpen, setIsModalOpen] = useState(false);
  // حالة لتخزين بيانات التحليل المحدد
  const [selectedTest, setSelectedTest] = useState(null);
  // حالة للتحكم في نوع النموذج داخل النافذة: 'details' أو 'payment'
  const [formType, setFormType] = useState('details');
  // حالة لرسالة النجاح بعد إرسال النموذج
  const [isSubmitted, setIsSubmitted] = useState(false);
  // حالة لبيانات الدفع
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  });

  // بيانات التحاليل الكاملة
  const allTests = [
    {
      icon: <Droplet className="w-8 h-8 text-white" />,
      title: "تحاليل الدم",
      description: "فحص شامل لخلايا الدم ومكوناتها للكشف عن الأمراض المختلفة.",
      details: "يساعد هذا التحليل في تشخيص فقر الدم والالتهابات وبعض أمراض الدم الأخرى.",
      price: "150 جنيه",
      preparation: "لا يتطلب تحضيرات خاصة.",
      color: "bg-red-500"
    },
    {
      icon: <Activity className="w-8 h-8 text-white" />,
      title: "تحاليل السكر",
      description: "قياس مستويات الجلوكوز في الدم لتشخيص ومتابعة مرض السكري.",
      details: "يشمل قياس السكر العشوائي والصائم والمراقب، ويساعد في تقييم مدى فعالية العلاج.",
      price: "100 جنيه",
      preparation: "الصيام من 8 إلى 12 ساعة قبل التحليل.",
      color: "bg-pink-500"
    },
    {
      icon: <Dna className="w-8 h-8 text-white" />,
      title: "تحاليل الحمض النووي",
      description: "فحوصات جينية متقدمة لتحديد الاستعداد الوراثي لبعض الحالات.",
      details: "يتم استخدامه لتشخيص الأمراض الوراثية وتحديد الأبوة.",
      price: "1500 جنيه",
      preparation: "لا يتطلب تحضيرات خاصة.",
      color: "bg-purple-500"
    },
    {
      icon: <HeartPulse className="w-8 h-8 text-white" />,
      title: "تحاليل الغدة الدرقية",
      description: "تقييم وظائف الغدة الدرقية والهرمونات المتعلقة بها.",
      details: "يشمل قياس هرمون TSH و T3 و T4، وهو ضروري لتشخيص قصور أو فرط نشاط الغدة الدرقية.",
      price: "250 جنيه",
      preparation: "لا يتطلب تحضيرات خاصة.",
      color: "bg-cyan-500"
    },
    {
      icon: <TestTube className="w-8 h-8 text-white" />,
      title: "تحاليل الكبد",
      description: "تقييم أداء الكبد ووظائفه الحيوية في الجسم.",
      details: "يقيس إنزيمات الكبد مثل ALT و AST، ويساعد في الكشف عن أمراض الكبد مثل الالتهاب الكبدي.",
      price: "200 جنيه",
      preparation: "الصيام من 4 إلى 6 ساعات.",
      color: "bg-green-500"
    },
    {
      icon: <FlaskConical className="w-8 h-8 text-white" />,
      title: "تحاليل الكلى",
      description: "فحص شامل لوظائف الكلى ومستويات الكرياتينين واليوريا.",
      details: "يساعد في تقييم كفاءة الكلى في تصفية الدم والكشف عن أي مشكلات محتملة.",
      price: "180 جنيه",
      preparation: "لا يتطلب تحضيرات خاصة.",
      color: "bg-blue-500"
    },
    {
      icon: <Pill className="w-8 h-8 text-white" />,
      title: "تحاليل الهرمونات",
      description: "قياس مستويات الهرمونات المختلفة في الجسم لتحديد أي خلل.",
      details: "تساعد في تشخيص مشاكل الغدة الدرقية، والغدة الكظرية، وغيرها من الحالات المرتبطة بالهرمونات.",
      price: "300 جنيه",
      preparation: "يعتمد على نوع الهرمون المطلوب قياسه. استشر الطبيب.",
      color: "bg-teal-500"
    },
    {
      icon: <Microscope className="w-8 h-8 text-white" />,
      title: "تحاليل الفيروسات",
      description: "الكشف عن وجود فيروسات معينة في الجسم مثل فيروسات الكبد الوبائي أو الإيدز.",
      details: "يستخدم هذا التحليل للكشف المبكر عن الإصابات الفيروسية وتقديم العلاج المناسب.",
      price: "350 جنيه",
      preparation: "لا يتطلب تحضيرات خاصة.",
      color: "bg-indigo-500"
    },
    {
      icon: <Wind className="w-8 h-8 text-white" />,
      title: "تحاليل الجهاز التنفسي",
      description: "فحوصات للرئة والجهاز التنفسي للكشف عن أمراض مثل الربو والتهاب الشعب الهوائية.",
      details: "يشمل قياس وظائف الرئة واختبارات الحساسية، ويساعد في تشخيص الأمراض المزمنة.",
      price: "220 جنيه",
      preparation: "قد يتطلب الامتناع عن بعض الأدوية قبل التحليل.",
      color: "bg-emerald-500"
    },
    {
      icon: <ShieldCheck className="w-8 h-8 text-white" />,
      title: "تحاليل المناعة",
      description: "تقييم كفاءة الجهاز المناعي وقدرته على مقاومة الأمراض.",
      details: "يساعد في تشخيص أمراض المناعة الذاتية والحساسية ونقص المناعة.",
      price: "400 جنيه",
      preparation: "لا يتطلب تحضيرات خاصة.",
      color: "bg-yellow-500"
    },
    {
      icon: <Syringe className="w-8 h-8 text-white" />,
      title: "تحاليل الحساسية",
      description: "تحديد المواد المسببة للحساسية مثل حبوب اللقاح أو الأطعمة.",
      details: "تساعد في تحديد مسببات الحساسية لتمكين المريض من تجنبها واتخاذ الإجراءات الوقائية.",
      price: "380 جنيه",
      preparation: "قد يتطلب الامتناع عن بعض الأدوية المضادة للهيستامين.",
      color: "bg-pink-700"
    },
    {
      icon: <Leaf className="w-8 h-8 text-white" />,
      title: "تحاليل الفيتامينات والمعادن",
      description: "فحص مستويات الفيتامينات والمعادن الأساسية في الجسم.",
      details: "يساعد في الكشف عن نقص الفيتامينات مثل فيتامين د وب12، ومعادن مثل الحديد.",
      price: "450 جنيه",
      preparation: "لا يتطلب تحضيرات خاصة.",
      color: "bg-orange-500"
    },
    {
      icon: <Bone className="w-8 h-8 text-white" />,
      title: "تحليل كثافة العظام",
      description: "قياس كثافة العظام لتقييم خطر الإصابة بهشاشة العظام.",
      details: "يتم استخدامه لتقييم صحة العظام والوقاية من الكسور.",
      price: "500 جنيه",
      preparation: "لا يتطلب تحضيرات خاصة.",
      color: "bg-gray-400"
    },
    {
      icon: <Sun className="w-8 h-8 text-white" />,
      title: "تحليل فيتامين د",
      description: "فحص مستوى فيتامين د في الدم، وهو ضروري لصحة العظام والمناعة.",
      details: "يساعد في تشخيص نقص فيتامين د، وهو أمر شائع ويؤثر على العديد من وظائف الجسم.",
      price: "320 جنيه",
      preparation: "لا يتطلب تحضيرات خاصة.",
      color: "bg-yellow-400"
    },
    {
      icon: <Syringe className="w-8 h-8 text-white" />,
      title: "لوحة الحساسية الغذائية",
      description: "تحديد الأطعمة التي تسبب تفاعلات حساسية أو عدم تحمل.",
      details: "يشمل اختبارات لأنواع متعددة من الأطعمة لتحديد مسببات الحساسية بدقة.",
      price: "600 جنيه",
      preparation: "قد يطلب منك الامتناع عن بعض الأطعمة قبل التحليل.",
      color: "bg-red-400"
    },
    {
      icon: <Radiation className="w-8 h-8 text-white" />,
      title: "تحليل المعادن الثقيلة",
      description: "فحص للكشف عن وجود مستويات سامة من المعادن الثقيلة في الجسم.",
      details: "مهم لمن يتعرضون للملوثات البيئية أو يظهرون أعراض التسمم بالمعادن.",
      price: "750 جنيه",
      preparation: "قد يتطلب عينة بول أو دم خاصة.",
      color: "bg-gray-700"
    },
  ];

  // دالة للتبديل بين عرض جميع التحاليل أو فقط الـ 8 الأولى
  const toggleShowAllTests = () => {
    setShowAllTests(!showAllTests);
  };

  // دالة لفتح النافذة المنبثقة وعرض التفاصيل
  const openModalWithDetails = (test) => {
    setSelectedTest(test);
    setIsModalOpen(true);
    setFormType('details');
    setIsSubmitted(false);
  };

  // دالة لفتح النافذة المنبثقة والانتقال مباشرة للدفع
  const openModalWithPayment = (test) => {
    setSelectedTest(test);
    setIsModalOpen(true);
    setFormType('payment');
    setIsSubmitted(false);
  };

  // دالة لإغلاق النافذة المنبثقة
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTest(null);
  };

  // دالة للتعامل مع تغييرات نموذج الدفع
  const handlePaymentChange = (e) => {
    setPaymentData({ ...paymentData, [e.target.name]: e.target.value });
  };

  // دالة للتعامل مع إرسال نموذج الدفع
  const handlePaymentSubmit = (e) => {
    e.preventDefault();
    console.log("Payment submitted for:", {
      test: selectedTest.title,
      price: selectedTest.price,
      paymentData: paymentData
    });
    setIsSubmitted(true);
  };

  const testsToDisplay = showAllTests ? allTests : allTests.slice(0, 8);

  return (
    <section id="tests" className="py-24 bg-slate-50 font-sans text-right min-h-screen">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 text-teal-700 text-sm font-semibold px-4 py-1.5 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
            دليل التحاليل
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-blue-900 mb-5 tracking-tight">
            التحاليل الفردية
          </h2>
          <div className="w-16 h-1 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            نقدم مجموعة واسعة من التحاليل الفردية لتلبية احتياجاتك الصحية. اختر التحليل المناسب لك واعرف المزيد عنه قبل الحجز.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {testsToDisplay.map((test, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group"
            >
              <div className={`p-4 rounded-xl inline-flex ${test.color} group-hover:bg-opacity-90 transition-all duration-300`}>
                {test.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-800 mt-4 mb-2">
                {test.title}
              </h3>
              <p className="text-gray-600">
                {test.description}
              </p>
              <div className="mt-6 flex justify-between items-center">
                <button
                  onClick={() => openModalWithDetails(test)}
                  className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors"
                >
                  اعرف المزيد
                </button>
                <button
                  onClick={() => openModalWithPayment(test)}
                  className="bg-gradient-to-l from-blue-700 to-teal-600 text-white font-bold py-2 px-4 rounded-full text-sm hover:shadow-lg transition duration-300 shadow-sm"
                >
                  اطلب الآن
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button
            onClick={toggleShowAllTests}
            className="bg-white text-blue-800 border-2 border-blue-800 font-bold py-3 px-8 rounded-full hover:bg-gradient-to-l hover:from-blue-700 hover:to-teal-600 hover:text-white hover:border-transparent transition-all duration-300 shadow-sm"
          >
            {showAllTests ? 'عرض أقل' : `عرض جميع التحاليل`}
          </button>
        </div>
      </div>

      {/* النافذة المنبثقة (Modal) */}
      {isModalOpen && selectedTest && (
        <div className="fixed inset-0 bg-blue-950/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-auto relative transform transition-all duration-300 scale-100">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>

            {!isSubmitted ? (
              <>
                {formType === 'details' && (
                  <>
                    <div className="text-center mb-6">
                      <div className={`p-4 rounded-full inline-flex ${selectedTest.color} mb-4`}>
                        {selectedTest.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-blue-900">
                        {selectedTest.title}
                      </h3>
                    </div>
                    <div className="space-y-4 text-right mb-6">
                      <div>
                        <p className="text-gray-800 font-semibold">الوصف:</p>
                        <p className="text-gray-600 mt-1">{selectedTest.details}</p>
                      </div>
                      <div>
                        <p className="text-gray-800 font-semibold">تعليمات التحضير:</p>
                        <p className="text-gray-600 mt-1">{selectedTest.preparation}</p>
                      </div>
                      <div>
                        <p className="text-gray-800 font-semibold">السعر:</p>
                        <p className="text-gray-600 mt-1">{selectedTest.price}</p>
                      </div>
                    </div>
                    <div className="mt-8 text-center">
                      <button
                        onClick={() => setFormType('payment')}
                        className="w-full bg-gradient-to-l from-blue-700 to-teal-600 text-white font-bold py-3 px-8 rounded-full hover:shadow-lg transition duration-300 shadow-md"
                      >
                        اطلب هذا التحليل الآن
                      </button>
                    </div>
                  </>
                )}

                {formType === 'payment' && (
                  <>
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-blue-900">
                        إتمام عملية الدفع
                      </h3>
                      <p className="text-lg text-gray-600 mt-2">
                        إجمالي المبلغ: {selectedTest.price}
                      </p>
                    </div>
                    {/* نموذج الدفع */}
                    <form onSubmit={handlePaymentSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700">
                          رقم البطاقة
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <input
                            type="text"
                            id="cardNumber"
                            name="cardNumber"
                            value={paymentData.cardNumber}
                            onChange={handlePaymentChange}
                            required
                            className="block w-full pr-10 rounded-md border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <CreditCard className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="w-1/2">
                          <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700">
                            تاريخ الانتهاء (MM/YY)
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <input
                              type="text"
                              id="expiryDate"
                              name="expiryDate"
                              value={paymentData.expiryDate}
                              onChange={handlePaymentChange}
                              required
                              placeholder="MM/YY"
                              className="block w-full pr-10 rounded-md border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                              <Calendar className="h-5 w-5 text-gray-400" />
                            </div>
                          </div>
                        </div>
                        <div className="w-1/2">
                          <label htmlFor="cvv" className="block text-sm font-medium text-gray-700">
                            رمز الأمان (CVV)
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <input
                              type="text"
                              id="cvv"
                              name="cvv"
                              value={paymentData.cvv}
                              onChange={handlePaymentChange}
                              required
                              className="block w-full pr-10 rounded-md border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                              <Lock className="h-5 w-5 text-gray-400" />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-center mt-6">
                        <button
                          type="submit"
                          className="w-full bg-gradient-to-l from-blue-700 to-teal-600 text-white font-bold py-3 px-8 rounded-full hover:shadow-lg transition duration-300 shadow-md"
                        >
                          ادفع {selectedTest.price}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </>
            ) : (
              <div className="text-center p-8">
                <CheckCircle className="w-16 h-16 mx-auto text-teal-500 mb-4" />
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  تمت عملية الدفع بنجاح!
                </h3>
                <p className="text-gray-600">
                  سنتواصل معك في أقرب وقت لتأكيد موعد التحليل.
                </p>
                <button
                  onClick={closeModal}
                  className="mt-6 bg-gradient-to-l from-blue-700 to-teal-600 text-white font-bold py-2 px-6 rounded-full hover:shadow-lg transition duration-300"
                >
                  إغلاق
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default IndividualTests;
